import React from 'react';
import { Box, Button, Card, CardContent, Chip, Container, Grid, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PlaceIcon from '@mui/icons-material/Place';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

const participantSteps = [
  'Scan the event QR code and log in if needed.',
  'Check in to the event so the system knows you are physically there.',
  'Wait for the event owner to assign your trade group to a location.',
  'Go to your assigned location and confirm you are physically at that table.',
  'Inspect the item(s) you are receiving before confirming completion.',
  'If everything is good, confirm receipt. If something is wrong, report the issue instead.',
  'After the session ends, leave a quick review.'
];

const adminSteps = [
  'Define event locations/tables before participants arrive.',
  'Encourage participants to scan the event QR and check in on arrival.',
  'Watch trade group readiness to see which groups are ready to start.',
  'Start a trade session by assigning a ready group to an available location.',
  'Monitor staged vs active vs completed vs canceled sessions during the event.',
  'Handle issue reports, cancellations, and unfinished groups as they happen.',
  'Close out the event and record cleanup reasons for anything unfinished.'
];

export default function EventDayGuidePage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 4,
            color: 'white',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          <Stack spacing={2}>
            <Chip label="Event-Day Guide" sx={{ width: 'fit-content', color: 'white', backgroundColor: 'rgba(255,255,255,0.18)', fontWeight: 700 }} />
            <Typography variant="h3" sx={{ fontWeight: 800 }}>
              How CrowdTrades works on event day
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.95, maxWidth: 900, fontWeight: 400 }}>
              A plain-English guide for both participants and event owners so people know what to do when the in-person trading actually starts.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/faq')} sx={{ backgroundColor: 'white', color: '#5b63d3', fontWeight: 700 }}>
                Read main FAQ
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <QrCode2Icon sx={{ color: '#667eea', fontSize: 36 }} />
                  <Typography variant="h6">QR-first arrival flow</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Participants scan the event QR to authenticate, check in, and later confirm they reached their assigned location.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <PlaceIcon sx={{ color: '#667eea', fontSize: 36 }} />
                  <Typography variant="h6">Location-managed sessions</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ready trade groups are assigned to real locations so the owner can see what is happening live instead of guessing.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <TaskAltIcon sx={{ color: '#667eea', fontSize: 36 }} />
                  <Typography variant="h6">Completion + issue path</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sessions can end cleanly with completion confirmations or safely with issue reporting and cleanup logging.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 4, height: '100%' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <PeopleAltIcon sx={{ color: '#667eea' }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Participant guide
                </Typography>
              </Stack>
              <List disablePadding>
                {participantSteps.map((step, index) => (
                  <ListItem key={step} disableGutters sx={{ alignItems: 'flex-start', py: 1 }}>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 600 }}>Step {index + 1}</Typography>}
                      secondary={<Typography variant="body2" color="text.secondary">{step}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
              <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 3, backgroundColor: '#f8fbff' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ReportProblemIcon sx={{ color: '#ed6c02' }} />
                  <Typography variant="body2" color="text.secondary">
                    If the item is missing, damaged, or not what was expected, do not confirm completion. Use <strong>Report Issue</strong> so the admin can unwind the session properly.
                  </Typography>
                </Stack>
              </Paper>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 4, height: '100%' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <AdminPanelSettingsIcon sx={{ color: '#667eea' }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Event owner guide
                </Typography>
              </Stack>
              <List disablePadding>
                {adminSteps.map((step, index) => (
                  <ListItem key={step} disableGutters sx={{ alignItems: 'flex-start', py: 1 }}>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 600 }}>Step {index + 1}</Typography>}
                      secondary={<Typography variant="body2" color="text.secondary">{step}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
              <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 3, backgroundColor: '#fff8f1' }}>
                <Typography variant="body2" color="text.secondary">
                  The point of the admin controls is not just data entry. They are there to help you keep the room moving, see which groups are blocked, and leave a usable audit trail when something goes sideways.
                </Typography>
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
